import { Flex, Spacing, Tag, Text, useModal } from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useInvoiceComplianceMode } from '@features/invoices/hooks/useInvoiceComplianceMode'
import { VatExemptionMention } from '@features/invoicingSettings/elements/VatExemptionMention'
import { useZervantSettings } from '@features/invoicingSettings/hooks/useZervantSettings'
import { TaxRateGroup } from '@features/vatRates/types/taxRateGroup'

import { ModalId } from '../../../../../../../../../../../../enums/modalId'

interface InvoiceSummaryLineProps {
  label: string
  text: string
  region?: TaxRateGroup
}

export const InvoiceSummaryLine = ({ label, text, region }: InvoiceSummaryLineProps): ReactElement => {
  const { t } = useTranslation()
  const { data: zervantSettings } = useZervantSettings()
  const { isFrenchComplianceMode } = useInvoiceComplianceMode()

  const { open: openVatExemptionMentionModal } = useModal(ModalId.VatExemptionMentionModal)
  const { open: openVatExemptionMentionInfoModal } = useModal(ModalId.VatExemptionMentionInfoModal)

  const vatExemptionMentionScheme = zervantSettings?.values?.company?.legalMentions?.custom?.vatExemptionScheme ?? ''

  const isLocalVatRate = region === 'local'
  const showVatSchemeInfo = region !== undefined && isFrenchComplianceMode

  const title = useMemo(() => {
    switch (region) {
      case 'local':
        return t('external_invoices.editor.summary.vat.scheme.activity_based')
      case 'eu':
        return t('external_invoices.editor.summary.vat.scheme.eu_sales')
      case 'world':
        return t('external_invoices.editor.summary.vat.scheme.non_eu_sales')
    }
  }, [region, t])

  const handleOpenModal = useCallback(() => {
    if (isLocalVatRate) {
      openVatExemptionMentionModal()
    } else {
      openVatExemptionMentionInfoModal()
    }
  }, [isLocalVatRate, openVatExemptionMentionModal, openVatExemptionMentionInfoModal])

  return (
    <Flex flexDirection="column" gap={Spacing.S12}>
      <Flex justifyContent="space-between">
        <Flex gap={Spacing.S8} alignItems="center">
          <Text colorVariant="secondary" variant="bodySM">
            {label}
          </Text>
          {showVatSchemeInfo && (
            <Tag color="neutral" size="sm" type="subtle">
              {title}
            </Tag>
          )}
        </Flex>
        <Text colorVariant="secondary" variant="bodySM">
          {text}
        </Text>
      </Flex>
      {showVatSchemeInfo && (
        <VatExemptionMention
          onOpenModal={handleOpenModal}
          vatExemptionMentionScheme={vatExemptionMentionScheme}
          isLocalVatRate={isLocalVatRate}
        />
      )}
    </Flex>
  )
}
